import queryString from 'query-string';
import SequenceUUID from 'sequential-uuid';
import {
  isEqual,
  isEmpty,
  isNull,
  isUndefined,
  transform,
  isObject,
  isString,
} from 'lodash';
import { Node } from 'graphql/types';

const uuidV4 = new SequenceUUID({
  valid: true,
  dashes: false,
  unsafeBuffer: false,
});

export const notEqual = (first, second) => isEqual(first, second) === false;

export const notEmpty = (...params) => isEmpty(...params) === false;

export const notNull = (value) => isNull(value) === false;

export const isDefined = (value) => isUndefined(value) === false;

export function isInt(number) {
  return Number(number) === number && number % 1 === 0;
}

export function isFloat(number) {
  return Number(number) === number && number % 1 !== 0;
}

export function asyncAction(promise) {
  return Promise.resolve(promise)
    .then((data) => [null, data])
    .catch((error) => [error]);
}

export function lowerCaseFirst(string = '') {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

export const parseQueryString = (search) => queryString.parse(search);

export const stringifyQueryParams = (query) => queryString.stringify(query);

export const jsonParseObjectValues = (object) => {
  return Object.entries(object).reduce(
    (object, [name, value]) => ({
      ...object,
      [name]: isString(value) ? JSON.parse(value as any) : value,
    }),
    {},
  );
};

export const jsonStringifyObjectValues = (object) => {
  return Object.entries(object).reduce(
    (object, [name, value]) => ({
      ...object,
      [name]: JSON.stringify(value),
    }),
    {},
  );
};

export function copyToClipboard(value: string): void {
  // Create new element
  let textArea = document.createElement('textarea');
  // Set value (string to be copied)
  textArea.value = value;
  // Set non-editable to avoid focus and move outside of view
  textArea.setAttribute('readonly', '');

  textArea.style['position'] = 'absolute';
  textArea.style['left'] = '-9999px';

  document.body.appendChild(textArea);
  // Select text inside element
  textArea.select();
  // Copy text to clipboard
  document.execCommand('copy');
  // Remove temporary element
  document.body.removeChild(textArea);
}

export function difference(object, base) {
  function changes(object, base) {
    return transform(object, function (result, value, key) {
      if (!isEqual(value, base[key])) {
        // @ts-ignore
        result[key] =
          isObject(value) && isObject(base[key])
            ? changes(value, base[key])
            : value;
      }
    });
  }
  return changes(object, base);
}

export function ucFirst(string: string): string {
  return string[0].toUpperCase() + string.substring(1);
}

export function differenceBetweenNumbers(a: number, b: number): number {
  return a > b ? a - b : b - a;
}

export function generateSequentialUUID(): string {
  return uuidV4.generate();
}

export function getBrowserTextSelection(): string | undefined {
  return window.getSelection()?.toString();
}

export function textIncludesWindowSelection(text?: string) {
  return (
    text?.includes(window.getSelection()?.toString() as string) ||
    text?.includes(getBrowserTextSelection() as string)
  );
}

export function isValidEmail(email: string): boolean {
  return /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\d-AZa-z-]+\.)+[A-Za-z]{2,}))$/.test(
    email,
  );
}

export function getArrayDuplicates(array: string[]): string[] {
  let duplicates = [...array];

  [...new Set(array)].forEach((item) => {
    duplicates = duplicates
      .slice(0, duplicates.indexOf(item))
      .concat(
        duplicates.slice(duplicates.indexOf(item) + 1, duplicates.length),
      );
  });

  return duplicates;
}

export function doesNotInclude(value: string | number, array: any[]): boolean {
  return array.includes(value as never) === false;
}

export function generateRandomColor(): string {
  return Math.floor(Math.random() * 16777215).toString(16);
}

export function filterOutNodesInPlaceholder<T = Node>(nodes: T[]): T[] {
  const containingNodes = nodes
    // @ts-ignore
    .map((node) => node.contains)
    .filter(Boolean);

  return nodes.filter(
    // @ts-ignore
    (node) => containingNodes.some(({ id }) => id === node?.id) === false,
  );
}
